<template>
  <page-view :is-loading="loadings.isLoadingVideos">
    <template #header>
      <base-header :title="$t('message.pages.AbTest.AbTest')" has-intro>
        <template #actions v-if="hasEditAbTestPerm">
          <base-btn
            :data-intro="$t('message.introJS.testAb.create')"
            class="ml-4 primary white--text"
            @click="openCreateTestModal = true"
          >
            {{ $t('message.pages.AbTest.emptyState.addTest') }}
          </base-btn>
        </template>
      </base-header>
    </template>

    <template #content>
      <v-row no-gutters>
        <v-col
          v-if="hasEditAbTestPerm"
          v-show="selectedItems.length"
          :cols="isMobileBreakpoint || isTabletBreakpoint ? 12 : 'auto'"
        >
          <div class="buttons">
            <base-btn
              :disabled="selectedItems.length > 1"
              @click="handleOpenCodeModal"
            >
              Embed
            </base-btn>
            <base-btn color="red" @click="handleRemoveTest">
              {{ $t('message.remove') }}
            </base-btn>
          </div>
        </v-col>
        <v-col
          v-if="hasEditAbTestPerm"
          :cols="12"
          :order="'first'"
          class="ml-auto"
        >
          <SearchBar
            v-if="hasTests"
            :list="testList"
            :fuseOptions="searchFuseOptions"
            :placeholder="
              $t('message.pages.videosDataTable.searchBar.placeholder')
            "
          />
        </v-col>
      </v-row>

      <!-- data-table::header -->
      <v-card
        v-if="testList.length > 0"
        flat
        width="100%"
        height="45px"
        class="data-table__header"
        ref="dataTableHeader"
      >
        <v-row style="min-width: 1000px" class="d-flex" no-gutters>
          <!-- Checkbox -->
          <v-col cols="auto">
            <v-card
              class="d-flex align-center justify-start"
              height="45px"
              flat
              color="background"
              width="50px"
            >
              <v-simple-checkbox
                v-if="hasEditAbTestPerm"
                :color="!checkboxIndeterminateStatus ? 'primary' : ''"
                :indeterminate="checkboxIndeterminateStatus"
                :ripple="false"
                :value="anyItemSelected"
                @input="selectAllItems"
              ></v-simple-checkbox>
            </v-card>
          </v-col>

          <!-- Name -->
          <v-col>
            <v-card
              flat
              class="d-flex align-center justify-start"
              height="45px"
            >
              <v-hover v-slot="{ hover }">
                <v-row
                  no-gutters
                  justify="start"
                  align="center"
                  @click="handleOrderByName"
                >
                  <span class="caption mt-1"
                    ><b>{{
                      $t('message.pages.videosDataTable.header.name')
                    }}</b></span
                  >
                  <v-icon v-if="hover && !nameOrder" color="grey lighten-1"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && nameOrder === 'desc'"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && nameOrder === 'asc'"
                    >mdi-menu-up</v-icon
                  >
                </v-row>
              </v-hover>
            </v-card>
          </v-col>

          <!-- Status -->
          <v-col>
            <v-card
              flat
              class="d-flex align-center justify-start"
              height="45px"
            >
              <v-hover v-slot="{ hover }">
                <v-row
                  no-gutters
                  justify="start"
                  align="center"
                  @click="handleOrderByStatus"
                >
                  <span class="caption mt-1"><b>Status</b></span>
                  <v-icon v-if="hover && !statusOrder" color="grey lighten-1"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && statusOrder === 'desc'"
                    >mdi-menu-up</v-icon
                  >
                  <v-icon v-if="hover && statusOrder === 'asc'"
                    >mdi-menu-down</v-icon
                  >
                  <b
                    v-if="hover && statusOrder === 'ascd'"
                    class="ml-2 grey--text text--darken-1"
                    style="margin-top: 2px"
                    >-</b
                  >
                </v-row>
              </v-hover>
            </v-card>
          </v-col>

          <!-- Criado Em -->
          <v-col>
            <v-card
              flat
              class="d-flex align-center justify-start"
              height="45px"
            >
              <v-hover v-slot="{ hover }">
                <v-row
                  no-gutters
                  justify="start"
                  align="center"
                  @click="handleOrderByTime"
                >
                  <span class="caption mt-1"
                    ><b>{{ $t('message.pages.AbTest.createdAt') }}</b></span
                  >
                  <v-icon v-if="hover && !timeOrder" color="grey lighten-1"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && timeOrder === 'desc'"
                    >mdi-menu-down</v-icon
                  >
                  <v-icon v-if="hover && timeOrder === 'asc'"
                    >mdi-menu-up</v-icon
                  >
                </v-row>
              </v-hover>
            </v-card>
          </v-col>
        </v-row>
      </v-card>

      <!-- empty -->
      <div v-else-if="testList.length <= 0" class="mr-5 ml-7 mt-4">
        <v-row
          class="d-flex flex-column"
          no-gutters
          justify="center"
          align="center"
        >
          <img
            v-if="$vuetify.theme.dark"
            class="ml-4"
            src="@/assets/pandaLogo.svg"
            max-width="180"
          />
          <img
            v-else
            class="ml-4"
            src="@/assets/pandaHappy.svg"
            max-width="180"
          />
          <h3
            class="noFilesAddedText mb-2"
            style="color: #898989; font-size: 28px; font-weight: 500"
            v-html="$t('message.pages.AbTest.noTests')"
          ></h3>
          <span
            class="noFilesAddedText"
            style="color: #898989; font-size: 18px"
          >
            {{ $t('message.pages.AbTest.emptyState.createAbTest') }}
          </span>
          <span
            class="noFilesAddedText"
            style="color: #898989; font-size: 18px"
            v-html="$t('message.pages.AbTest.emptyState.phrase')"
          >
          </span>
          <v-btn
            outlined
            class="mt-5"
            color="primary"
            :disabled="!hasEditAbTestPerm"
            @click="openCreateTestModal = true"
          >
            {{ $t('message.pages.AbTest.emptyState.actionButton') }}
          </v-btn>
          <a class="mt-5" href="#" @click="openAbTestArticle">
            {{ $t('message.pages.AbTest.emptyState.knowMore') }} &raquo;
          </a>
        </v-row>
        <v-col class="d-flex justify-end align-end" v-if="hasEditAbTestPerm">
          <img class="svgArrowUp pb-16 mb-10" src="@/assets/arrowUp.svg" />
        </v-col>
      </div>

      <!-- data-table::content -->
      <vue-virtual-table
        v-if="testList.length > 0"
        ref="dataTableContent"
        class="pointerTable displayBlock data-table__content"
        :class="{
          virtualTable: $vuetify.theme.dark,
        }"
        :config="tableConfig"
        :data="testList"
        :height="tableHeight"
        :itemHeight="48"
        :minWidth="1000"
        :data-intro="
          !selectedItems.length ? $t('message.introJS.testAb.list') : null
        "
        @click="handleClickRow"
      >
        <template slot-scope="scope" slot="selectCommon">
          <v-simple-checkbox
            v-if="hasEditAbTestPerm"
            color="primary"
            :ripple="false"
            :value="checkIfSelected(scope.row)"
            @input="checkboxSelection(scope.row, scope.index)"
          ></v-simple-checkbox>
        </template>
        <template slot-scope="scope" slot="titleCommon">
          <v-row
            @click="handleClickRow(scope.row)"
            style="height: 100%"
            class="pt-1"
            no-gutters
            justify="start"
            align="center"
          >
            <v-icon style="margin-right: 5px" color="turquisePanda">
              mdi-play-circle-outline
            </v-icon>
            <span
              class="text-truncate"
              style="max-width: 280px"
              :title="scope.row.name"
            >
              {{ scope.row.name }}
            </span>
          </v-row>
        </template>

        <template slot-scope="scope" slot="statusCommom">
          <v-row
            @click="handleClickRow(scope.row)"
            class="pt-1"
            no-gutters
            justify="start"
            align="center"
          >
            <div class="status-container">
              <p
                class="status-button mb-0"
                :class="getStatus(scope.row.status).classColor"
              >
                {{ getStatus(scope.row.status).label }}
              </p>
            </div>
          </v-row>
        </template>

        <template slot-scope="scope" slot="createdAtCommom">
          <v-row
            @click="handleClickRow(scope.row)"
            class="pt-1"
            no-gutters
            justify="start"
            align="center"
          >
            <span class="text-center">
              {{
                new Date(scope.row.created_at)
                  .toLocaleString('pt-BR')
                  .slice(0, 3) +
                new Date(scope.row.created_at)
                  .toLocaleString(localeString, { month: 'long' })
                  .slice(0, 3) +
                '/' +
                new Date(scope.row.created_at)
                  .toLocaleString('pt-BR')
                  .slice(6, 10)
              }}
              {{ $t('message.pages.AbTest.at') }}
              {{
                `${getDateWithZero(
                  new Date(scope.row.created_at).getHours()
                )}:${getDateWithZero(
                  new Date(scope.row.created_at).getMinutes()
                )}`
              }}
            </span>
          </v-row>
        </template>
      </vue-virtual-table>

      <!-- dialogs -->
      <remove-test-modal
        :isRemovingTest="loadings.isRemovingTest"
        :openRemove="openRemove"
        :quantityToRemove="selectedItems.length"
        @close="openRemove = false"
        @remove="handleClickRemoveTest"
      />
      <v-dialog v-model="openCodeModal" width="600px">
        <embed-video
          v-if="openCode"
          :showMenuPosition="embedPosition"
          :onlyPageOptions="true"
          type="comparison"
          :showMenuEmbed="openCode"
          :video="{
            video_external_id: modalEmbed.id,
            pullzone_name: userPullzone,
            status: 'CONVERTED',
            video_player: `https://player-${userPullzone}.tv.pandavideo.com.br/embed/?v=${modalEmbed.id}`,
          }"
          :videoId="modalEmbed.id"
          :isAbTest="true"
          @showEmbed="(val) => (openCode = val)"
        />
      </v-dialog>
      <create-test-modal
        :openCreateModal="openCreateTestModal"
        :isCreating="loadings.isCreatingTest"
        :title="$t('message.pages.AbTest.createNewTest')"
        @close="openCreateTestModal = false"
        @create="(testName) => handleCreateTest(testName)"
      />
    </template>
  </page-view>
</template>

<script>
// external imports
import VueVirtualTable from 'vue-virtual-table'
import { mapState, mapMutations } from 'vuex'

// internal imports
import i18n from '@/translations/lang'
import introMixin from '@/utils/intro.mixin.js'

// API
import comparisonApi from '@/services/comparisonApi'

// Components
import EmbedVideo from '@/components/EmbedVideo.vue'
import RemoveTestModal from '@/views/AbTest/index/modals/RemoveTestModal.vue'
import CreateTestModal from '@/components/CreateModal.vue'
import PageView from '@/layout/PageView.vue'
import SearchBar from '@/components/SearchBar.vue'

export default {
  name: 'AbTest',
  components: {
    VueVirtualTable,
    EmbedVideo,
    RemoveTestModal,
    SearchBar,
    CreateTestModal,
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },
  mixins: [introMixin],
  data: () => ({
    searchFuseOptions: {
      keys: ['name', 'id'],
      includeScore: true,
      threshold: 0.4,
    },
    loadings: {
      isLoadingVideos: true,
      isRemovingTest: false,
      isCreatingTest: false,
    },
    statusLabels: {
      COMPLETED: {
        label: i18n.t('message.pages.AbTest.completed'),
        classColor: 'completed--status-button',
      },
      DRAFT: {
        label: i18n.t('message.pages.AbTest.draft'),
        classColor: 'draft--status-button',
      },
      IN_PROGRESS: {
        label: i18n.t('message.pages.AbTest.inProgress'),
        classColor: 'inprogress--status-button',
      },
    },
    embedPosition: {
      x: window.innerWidth / 2 - 300,
      y: 72,
    },
    openCreateTestModal: false,
    hasTests: false,
    openCode: false,
    openCodeModal: false,
    nameOrder: 'asc',
    statusOrder: 'asc',
    timeOrder: 'asc',
    tableHeight: window.innerHeight - 120,
    openRemove: false,
    modalEmbed: '',
    toBeRemoved: null,
    tests: [],
    resizeWindowTimeout: null,
    userPullzone: '',
    selectedItems: [],
    tableConfig: [
      {
        prop: '_action',
        actionName: 'selectCommon',
        alignItems: 'start',
        width: 50,
        justifyContent: 'start',
      },
      {
        prop: '_action',
        name: i18n.t('message.name'),
        sortable: true,
        actionName: 'titleCommon',
        alignItems: 'start',
      },
      {
        prop: '_action',
        name: 'status',
        actionName: 'statusCommom',
        alignItems: 'center',
      },
      {
        prop: '_action',
        actionName: 'createdAtCommom',
        name: 'Criado em',
        sortable: true,
        alignItems: 'center',
      },
    ],
  }),
  watch: {
    nameOrder: function () {
      this.orderByName()
    },
    statusOrder: function () {
      this.orderByStatus()
    },
    timeOrder: function () {
      this.orderByTime()
    },
    'selectedItems.length': {
      handler(value) {
        if (!value) return

        this.setIntroJS({
          key: 'test-ab-selected',
          delay: 350,
        })
      },
      deep: true,
      immediate: true,
    },
    '$vuetify.theme.dark'() {
      this.$nextTick(() => {
        const tableEl = this.$refs.dataTableContent?.$el
        if (!tableEl) return
        tableEl.style.display = 'none'
        tableEl.offsetHeight
        tableEl.style.display = ''
      })
    },
  },
  mounted() {
    this.$root.$on('changeLanguage', this.updateText)
    window.addEventListener('resize', this.resizeTimeout)
    this.syncListScrollWithHeaderScroll()
    try {
      comparisonApi.getComparisons().then(({ data }) => {
        this.tests = data.comparisons
        if (this.tests.length > 0) {
          this.orderByStatus()
          this.hasTests = true
        }
        this.userPullzone = data.pullzone
        this.loadings.isLoadingVideos = false
        this.setIntroJS({ key: 'test-ab', delay: 1500 })
      })
    } catch (e) {
      console.error(e)
    }

    this.hasEditAbTestPerm()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeTimeout)
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    resizeTimeout() {
      clearTimeout(this.resizeWindowTimeout)
      this.resizeWindowTimeout = setTimeout(() => {
        this.tableHeight = window.innerHeight - 185
      }, 300)
    },
    updateText() {
      this.statusLabels = {
        COMPLETED: {
          label: i18n.t('message.pages.AbTest.completed'),
          classColor: 'completed--status-button',
        },
        DRAFT: {
          label: i18n.t('message.pages.AbTest.draft'),
          classColor: 'draft--status-button',
        },
        IN_PROGRESS: {
          label: i18n.t('message.pages.AbTest.inProgress'),
          classColor: 'inprogress--status-button',
        },
      }
    },
    syncListScrollWithHeaderScroll() {
      const _this = this
      if (!this.$refs.dataTableContent?.$el) {
        setTimeout(() => {
          _this.syncListScrollWithHeaderScroll()
        }, 100)
        return
      }
      this.$refs.dataTableContent.$el.onscroll = () => {
        _this.$refs.dataTableHeader.$el.scrollLeft =
          this.$refs.dataTableContent.$el.scrollLeft
      }
    },
    hasEditAbTestPerm() {
      return (
        this.$store.state.user?.userPermissions?.includes('put-test-a-b') ??
        false
      )
    },
    openAbTestArticle() {
      if (this.locale === 'pt') {
        return window.open(
          'https://help.pandavideo.com/pt-br/article/como-funciona-o-teste-ab-12xaro6/',
          '_blank'
        )
      }
      return window.open(
        'https://help.pandavideo.com/en-us/article/how-the-test-ab-works-1u9l8iv/',
        '_blank'
      )
    },
    selectAllItems() {
      if (this.selectedItems.length === this.testList.length) {
        this.selectedItems = []
        return
      }
      this.selectedItems = [...this.testList]
    },
    checkIfSelected(item) {
      let foundItem = this.selectedItems.find((searchedItem) => {
        return searchedItem.id === item.id
      })
      if (foundItem) return true
      return false
    },
    checkboxSelection(item) {
      let alreadySelected = this.checkIfSelected(item)
      if (alreadySelected) {
        let newArr = [
          ...this.selectedItems.filter((comparison) => {
            return comparison.id !== item.id
          }),
        ]
        this.selectedItems = [...newArr]
      } else {
        this.selectedItems.push(item)
      }
    },
    async handleClickRemoveTest() {
      try {
        this.loadings.isRemovingTest = true
        await comparisonApi.deleteComparison(this.toBeRemoved)
        this.tests = this.tests.filter((test) => {
          return this.toBeRemoved.indexOf(test.id) === -1
        })
        this.selectedItems = []
        this.loadings.isRemovingTest = false
        this.toBeRemoved = null
        this.openRemove = false
        const tests = this.tests
        if (Array.isArray(tests) && !tests.length) {
          this.hasTests = false
        }
      } catch (e) {
        this.loadings.isRemovingTest = false
        this.handleSnackbar(i18n.t('message.pages.AbTest.errors.removeTest'))
      }
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.navDrawer.successMessage')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
    handleClickRow(item) {
      if (item.name)
        this.$router.push({ name: 'NewTest', params: { id: item.id } })
    },
    getStatus(status) {
      return this.statusLabels[status]
    },
    async handleCreateTest(name) {
      try {
        this.loadings.isCreatingTest = true
        const { data } = await comparisonApi.createComparison({
          name,
          description: '',
          video_ids: [],
        })
        this.loadings.isCreatingTest = false
        this.openCreateTestModal = false
        this.$router.push({ name: 'NewTest', params: { id: data.id } })
      } catch (e) {
        console.error(e)
        this.handleSnackbar(i18n.t('message.pages.AbTest.errors.createTest'))
        this.loadings.isCreatingTest = false
      }
    },
    handleOpenCodeModal() {
      this.openCodeModal = true
      this.modalEmbed = this.selectedItems[0]
      setTimeout(() => {
        this.openCode = true
      })
    },
    getDateWithZero(number) {
      if (number < 10) return `0${number}`
      return number
    },
    handleRemoveTest() {
      this.toBeRemoved = this.selectedItems.map((item) => item.id)
      this.openRemove = true
    },
    orderByTime() {
      if (this.timeOrder === 'asc') {
        this.tests.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        )
      }
      if (this.timeOrder === 'desc') {
        this.tests.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
      }
    },
    handleOrderByTime() {
      if (!this.timeOrder || this.timeOrder === 'desc')
        return (this.timeOrder = 'asc')
      if (this.timeOrder === 'asc') return (this.timeOrder = 'desc')
    },
    orderByStatus() {
      if (this.statusOrder === 'asc') {
        this.tests.sort((a, b) => a.status.localeCompare(b.status))
      }
      if (this.statusOrder === 'desc') {
        this.tests.sort((a, b) => -a.status.localeCompare(b.status))
      }
      if (this.statusOrder === 'ascd') {
        this.tests.sort((a, b) => {
          if (a.status === 'DRAFT') {
            return -1
          }
          if (b.status === 'DRAFT') {
            return 1
          }
        })
      }
    },
    handleOrderByStatus() {
      if (!this.statusOrder || this.statusOrder === 'desc')
        return (this.statusOrder = 'asc')
      if (this.statusOrder === 'asc') return (this.statusOrder = 'ascd')
      if (this.statusOrder === 'ascd') return (this.statusOrder = 'desc')
    },
    orderByName() {
      if (this.nameOrder === 'asc') {
        this.tests.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
      if (this.nameOrder === 'desc') {
        this.tests.sort(
          (a, b) => -a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
    },
    handleOrderByName() {
      if (!this.nameOrder || this.nameOrder === 'desc')
        return (this.nameOrder = 'asc')
      if (this.nameOrder === 'asc') return (this.nameOrder = 'desc')
    },
  },
  computed: {
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
      isTabletBreakpoint: (state) => state.user.isTabletBreakpoint,
    }),
    anyItemSelected() {
      return this.selectedItems.length > 0
    },
    checkboxIndeterminateStatus() {
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.length < this.testList.length
      )
    },
    locale() {
      return this.$i18next.resolvedLanguage
    },
    localeString() {
      return this.locale === 'pt' ? 'pt-BR' : 'en-US'
    },
    testList() {
      return this.tests
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/autocomplete';
@import '@/assets/scss/src/data-tableV2';

::v-deep .item-line {
  border-top: 1px solid varFigma(neutral-95-lightest) !important;
}

::v-deep .item-cell-inner {
  display: flex !important;
}

.buttons {
  margin-bottom: $spacing-plus-1;

  display: grid;
  overflow-x: auto;

  grid-template-columns: repeat(4, auto);
  column-gap: $spacing-minus-2;
  row-gap: $spacing-minus-2;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    margin-bottom: initial;
  }
}
</style>

<style>
.data-table__content .virtual-scroller::-webkit-scrollbar {
  width: 4px;
  border-radius: 8px;
}

.data-table__content .virtual-scroller::-webkit-scrollbar-thumb {
  background-color: #d0d1d9 !important;
}

.data-table__content.virtualTable .virtual-scroller::-webkit-scrollbar-thumb {
  background-color: #52577a !important;
}

.icon-container {
  background-color: #eaedf6;
  padding: 4px;
  border-radius: 3px;
}

.mini-chip.v-chip.v-size--default {
  height: 23px;
  margin-bottom: 2px;
}

.t-header {
  display: none;
}

.t-header span {
  font-weight: bold;
}

.embed-textarea-customHtml .v-text-field__slot {
  margin: 0 -10px 4px 0 !important;
}

@keyframes removesMenu {
  from {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0% 100%);
  }
  to {
    clip-path: polygon(50% 0, 50% 0, 50% 100%, 50% 100%);
  }
}

@keyframes appearsMenu {
  from {
    clip-path: polygon(50% 0, 50% 0, 50% 100%, 50% 100%);
  }
  to {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0% 100%);
  }
}
</style>

<style scoped>
div.item-cell-inner.rowSlot {
  align-items: center !important;
}

.status-container {
  display: flex;
  justify-content: center;
}

.status-button {
  width: fit-content;
}
.svgArrowUp {
  position: relative;
  bottom: 435px;
  right: -10px;
}
</style>
